/**
 * @file Hooks de la messagerie des réservations côté dashboard : discussions, compteur de non lus, fil et actions.
 * @module packages.common.hooks.api.app.restaurants.ReservationMessages
 *
 * Il n'y a pas de temps réel : les lectures se rafraîchissent à intervalle
 * régulier tant que l'onglet est visible, et chaque action notifie les autres
 * onglets sur un canal dédié (`RESTAURANT.MESSAGES_UPDATED`), pour que le
 * compteur du menu, les pastilles du carnet et la discussion ouverte suivent.
 * Un changement de réservation les rafraîchit aussi.
 *
 * Pour que l'écran ne passe jamais par un vide : les autres filtres de la
 * liste et une discussion survolée sont préchargés dans le cache, et la liste
 * signale quand elle montre encore les résultats du filtre précédent.
 */

import {useCallback, useEffect, useMemo} from 'react';
import {useSWRConfig} from 'swr';
import axiosInstance, {endpoints} from 'packages/common/utils/axios';
import {useSnackbar} from 'packages/common/components/snackbar';
import {APP_EVENTS, notifyReservationsUpdated, notifyRestaurantMessagesUpdated} from 'packages/common/utils/appEventSync';
import {useSWRWithSync} from 'packages/common/hooks/useSWRWithSync';
import {
  CONVERSATION_BULK_ACTIONS,
  CONVERSATION_LIST_FILTERS,
  MESSAGES_REFRESH_INTERVAL_MS,
  UNREAD_SUMMARY_REFRESH_INTERVAL_MS,
} from 'packages/common/constants/restaurants/reservationMessages';

/**
 * Canaux qui rafraîchissent les lectures de la messagerie : ses propres
 * actions, et tout changement de réservation (statut et créneau affichés dans
 * la discussion, réservation supprimée qui sort la discussion de la liste).
 */
const MESSAGING_SYNC_EVENTS = [APP_EVENTS.RESTAURANT.MESSAGES_UPDATED, APP_EVENTS.RESTAURANT.RESERVATIONS_UPDATED];

/**
 * Lit la charge utile `data` d'une réponse de l'API messagerie.
 *
 * @param {string} url - URL à lire
 * @return {Promise<Object>} Données
 */
const fetchMessagingData = async (url) => {
  const response = await axiosInstance.get(url);
  return response.data.data;
};

/**
 * URL d'une page de discussions : la même pour la lecture et le préchargement, sinon le cache ne sert pas.
 *
 * @param {string} companyId - Établissement
 * @param {Object} params
 * @param {string} [params.filter] - Filtre de liste (CONVERSATION_LIST_FILTERS)
 * @param {string} [params.search] - Recherche sur le client
 * @param {number} [params.page=1] - Page, à partir de 1
 * @return {string} URL
 */
function buildConversationsUrl(companyId, {filter = CONVERSATION_LIST_FILTERS.ALL, search = '', page = 1} = {}) {
  const params = new URLSearchParams({filter, page: String(page)});
  if (search.trim()) params.set('search', search.trim());
  return `${endpoints.app.restaurant.messages.conversations(companyId)}?${params.toString()}`;
}

/**
 * Page de discussions de l'établissement, filtrable et cherchable.
 *
 * @param {string|null} companyId - Établissement (null désactive la lecture)
 * @param {Object} params
 * @param {string} [params.filter] - Filtre de liste (CONVERSATION_LIST_FILTERS)
 * @param {string} [params.search] - Recherche sur le client
 * @param {number} [params.page=1] - Page, à partir de 1
 * @return {Object} `{conversations, total, pageCount, isLoading, isSwitching, isError, mutate}` ; `isSwitching` :
 *   les résultats affichés sont encore ceux du filtre, de la recherche ou de la page précédents ;
 *   `isError` : la page demandée n'a pas pu être chargée (un rafraîchissement en échec garde la liste déjà là)
 */
export const useRestaurantConversations = (companyId, {filter = CONVERSATION_LIST_FILTERS.ALL, search = '', page = 1} = {}) => {
  const url = useMemo(() => (companyId ? buildConversationsUrl(companyId, {filter, search, page}) : null), [companyId, filter, search, page]);

  const {cache} = useSWRConfig();
  const {data, error, mutate, isLoading: isKeyLoading} = useSWRWithSync(url, fetchMessagingData, {
    eventTypes: MESSAGING_SYNC_EVENTS,
    companyId,
    swrOptions: {refreshInterval: MESSAGES_REFRESH_INTERVAL_MS, keepPreviousData: true},
  });

  return {
    conversations: data?.items || [],
    total: data?.total || 0,
    pageCount: data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1,
    isLoading: url !== null && !data && !error,
    // keepPreviousData : la clé courante charge encore alors que des données (précédentes) sont affichées
    isSwitching: Boolean(isKeyLoading && data),
    // Sans données propres à la page demandée, celles affichées seraient celles d'un autre filtre
    isError: Boolean(error) && cache.get(url)?.data === undefined,
    mutate,
  };
};

/**
 * Précharge la première page de filtres pas encore affichés, pour qu'y passer soit instantané.
 *
 * Le cache est rempli sans revalidation ; à l'affichage du filtre, la liste
 * se montre aussitôt puis se rafraîchit.
 *
 * @param {string|null} companyId - Établissement (null désactive le préchargement)
 * @param {Array<string>} filters - Filtres à précharger (CONVERSATION_LIST_FILTERS)
 * @param {*} [refreshKey] - Valeur dont le changement relance le préchargement (ex. nombre de non lues)
 */
export const usePrefetchRestaurantConversations = (companyId, filters, refreshKey) => {
  const {mutate} = useSWRConfig();
  const filtersKey = filters.join(',');

  useEffect(() => {
    if (!companyId) return;
    filtersKey.split(',').forEach((filter) => {
      const url = buildConversationsUrl(companyId, {filter});
      // Échec silencieux : le filtre se chargera normalement à l'affichage
      mutate(url, fetchMessagingData(url), {revalidate: false}).catch(() => {});
    });
  }, [companyId, filtersKey, refreshKey, mutate]);
};

/**
 * Nombre de discussions non lues et réservations concernées (menu, carnet).
 *
 * @param {string|null} companyId - Établissement (null désactive la lecture, ex. messagerie coupée)
 * @return {Object} `{unreadConversations, unreadReservationIds}` (`unreadReservationIds` est un Set)
 */
export const useRestaurantMessagesUnreadSummary = (companyId) => {
  const url = companyId ? endpoints.app.restaurant.messages.unreadSummary(companyId) : null;

  const {data} = useSWRWithSync(url, fetchMessagingData, {
    eventTypes: MESSAGING_SYNC_EVENTS,
    companyId,
    swrOptions: {refreshInterval: UNREAD_SUMMARY_REFRESH_INTERVAL_MS},
  });

  const unreadReservationIds = useMemo(() => new Set(data?.reservationIds || []), [data]);

  return {unreadConversations: data?.unreadConversations || 0, unreadReservationIds};
};

/**
 * Fil de la discussion d'une réservation, même vide.
 *
 * @param {string|null} companyId - Établissement
 * @param {string|null} reservationId - Réservation (null désactive la lecture)
 * @return {Object} `{thread, isLoading, isError, mutate}` ; `thread` porte `messagingEnabled`, `reservation`, `conversation`, `messages`
 */
export const useReservationThread = (companyId, reservationId) => {
  const url = companyId && reservationId ? endpoints.app.restaurant.messages.thread(companyId, reservationId) : null;

  const {data, error, mutate} = useSWRWithSync(url, fetchMessagingData, {
    eventTypes: MESSAGING_SYNC_EVENTS,
    companyId,
    swrOptions: {refreshInterval: MESSAGES_REFRESH_INTERVAL_MS},
  });

  return {
    thread: data || null,
    isLoading: url !== null && !data && !error,
    // Un rafraîchissement en échec garde le fil affiché, et avec lui le texte en cours de saisie
    isError: Boolean(error) && data === undefined,
    mutate,
  };
};

/**
 * Préchargement du fil d'une discussion, par exemple au survol de sa ligne.
 *
 * Un fil déjà en cache n'est pas redemandé ; il se rafraîchit à l'ouverture.
 *
 * @param {string|null} companyId - Établissement
 * @return {Function} `(reservationId) => void`
 */
export const usePrefetchReservationThread = (companyId) => {
  const {cache, mutate} = useSWRConfig();

  return useCallback(
      (reservationId) => {
        if (!companyId || !reservationId) return;
        const url = endpoints.app.restaurant.messages.thread(companyId, reservationId);
        if (cache.get(url)?.data !== undefined) return;
        // Échec silencieux : la discussion se chargera normalement à l'ouverture
        mutate(url, fetchMessagingData(url), {revalidate: false}).catch(() => {});
      },
      [companyId, cache, mutate],
  );
};

/**
 * Confirmation d'une action groupée, accordée au nombre de discussions.
 *
 * @param {string} action - CONVERSATION_BULK_ACTIONS
 * @param {number} count - Discussions sélectionnées
 * @return {string} Message de confirmation
 */
function bulkSuccessMessage(action, count) {
  const subject = count > 1 ? `${count} discussions` : 'Discussion';
  const plural = count > 1 ? 's' : '';
  if (action === CONVERSATION_BULK_ACTIONS.DELETE) return `${subject} supprimée${plural}`;
  if (action === CONVERSATION_BULK_ACTIONS.MARK_UNREAD) return `${subject} marquée${plural} comme non lue${plural}`;
  return `${subject} marquée${plural} comme lue${plural}`;
}

/**
 * Actions de l'équipe sur les discussions : répondre, marquer lu ou non lu, favori, supprimer la discussion.
 *
 * Chaque action réussie notifie les autres onglets. Une erreur affiche le
 * message de l'API puis est relancée, pour que l'appelant garde le texte saisi.
 *
 * @param {string} companyId - Établissement
 * @return {Object} `{sendMessage, markAsRead, markAsUnread, setFavorite, deleteConversation, applyBulkAction}`
 */
export const useReservationMessageActions = (companyId) => {
  const {enqueueSnackbar} = useSnackbar();

  const run = useCallback(
      async (request, {errorMessage, successMessage, reservationId, changesConversationPresence = false}) => {
        try {
          const response = await request();
          notifyRestaurantMessagesUpdated(companyId, {companyId, reservationId});
          // Discussion créée ou supprimée : la pastille du carnet et de la liste d'attente suit
          if (changesConversationPresence) notifyReservationsUpdated(companyId, {companyId, reservationId});
          if (successMessage) enqueueSnackbar(successMessage, {variant: 'success'});
          return response.data.data;
        } catch (error) {
          enqueueSnackbar(error.response?.data?.message || errorMessage, {variant: 'error'});
          throw error;
        }
      },
      [companyId, enqueueSnackbar],
  );

  const sendMessage = useCallback(
      (reservationId, body, seenUntil) =>
        run(() => axiosInstance.post(endpoints.app.restaurant.messages.thread(companyId, reservationId), {body, seenUntil}), {
          errorMessage: 'Le message n\'a pas pu être envoyé',
          reservationId,
          changesConversationPresence: true,
        }),
      [companyId, run],
  );

  // Silencieux : marquer lu à l'ouverture d'une discussion ne doit jamais déranger.
  // `seenUntil` : un message arrivé après le dernier affiché reste non lu
  const markAsRead = useCallback(
      async (reservationId, seenUntil) => {
        const response = await axiosInstance.post(endpoints.app.restaurant.messages.read(companyId, reservationId), {seenUntil});
        if (response.data.data.updated > 0) notifyRestaurantMessagesUpdated(companyId, {companyId, reservationId});
      },
      [companyId],
  );

  const markAsUnread = useCallback(
      (reservationId) =>
        run(() => axiosInstance.post(endpoints.app.restaurant.messages.unread(companyId, reservationId)), {
          errorMessage: 'La discussion n\'a pas pu être remise en non lu',
          successMessage: 'Discussion marquée comme non lue',
          reservationId,
        }),
      [companyId, run],
  );

  const setFavorite = useCallback(
      (reservationId, isFavorite) =>
        run(() => axiosInstance.put(endpoints.app.restaurant.messages.favorite(companyId, reservationId), {isFavorite}), {
          errorMessage: 'Le favori n\'a pas pu être enregistré',
          reservationId,
        }),
      [companyId, run],
  );

  // Jamais un message seul : la discussion entière, avec tous ses messages
  const deleteConversation = useCallback(
      (reservationId) =>
        run(() => axiosInstance.delete(endpoints.app.restaurant.messages.thread(companyId, reservationId)), {
          errorMessage: 'La discussion n\'a pas pu être supprimée',
          successMessage: 'Discussion supprimée',
          reservationId,
          changesConversationPresence: true,
        }),
      [companyId, run],
  );

  // Une seule requête pour toute la sélection ; le serveur passe ce qui ne s'y prête pas
  const applyBulkAction = useCallback(
      (action, reservationIds) =>
        run(() => axiosInstance.post(endpoints.app.restaurant.messages.bulk(companyId), {action, reservationIds}), {
          errorMessage: 'L\'action n\'a pas pu être appliquée aux discussions',
          successMessage: bulkSuccessMessage(action, reservationIds.length),
          changesConversationPresence: action === CONVERSATION_BULK_ACTIONS.DELETE,
        }),
      [companyId, run],
  );

  return {sendMessage, markAsRead, markAsUnread, setFavorite, deleteConversation, applyBulkAction};
};
